import JSZip from 'jszip'

// Taille max pour validation côté client (1 Go)
// Au-delà, la validation sera faite côté serveur après upload
const MAX_CLIENT_VALIDATION_SIZE = 1024 * 1024 * 1024

/**
 * Valide le contenu d'un fichier ZIP pour s'assurer qu'il contient les fichiers requis à la racine
 * Pour les fichiers > 1Go, skip la validation côté client (sera faite côté serveur)
 * @param {File} zipFile - Le fichier ZIP à valider
 * @param {Array<string>} requiredFiles - Liste des fichiers requis à la racine
 * @returns {Promise<{isValid: boolean, missingFiles: Array<string>, message: string, skippedClientValidation: boolean}>}
 */
export async function validateZipContents(zipFile, requiredFiles = []) {
  // Pour les gros fichiers, on skip la validation côté client
  // Elle sera faite côté serveur après l'upload
  if (zipFile.size > MAX_CLIENT_VALIDATION_SIZE) {
    return {
      isValid: true,
      missingFiles: [],
      rootFiles: [],
      rootFolders: [],
      message: 'Large file - validation will be done server-side',
      skippedClientValidation: true,
    }
  }

  try {
    const zip = new JSZip()
    const zipContents = await zip.loadAsync(zipFile)

    // Récupérer la liste des fichiers dans le ZIP
    const filesInZip = Object.keys(zipContents.files)

    // Filtrer pour obtenir seulement les fichiers à la racine (pas dans des dossiers)
    const rootFiles = filesInZip.filter((filename) => {
      const file = zipContents.files[filename]
      // Vérifier que c'est un fichier (pas un dossier) et qu'il est à la racine
      return !file.dir && !filename.includes('/')
    })

    // Filtrer pour obtenir les dossiers à la racine
    const rootFolders = filesInZip
      .filter((filename) => {
        const file = zipContents.files[filename]
        // Vérifier que c'est un dossier et qu'il est à la racine
        return file.dir && !filename.slice(0, -1).includes('/')
      })
      .map((folder) => folder.slice(0, -1)) // Supprimer le "/" final

    // Vérifier la présence des fichiers requis
    const missingFiles = requiredFiles.filter(
      (requiredFile) => !rootFiles.includes(requiredFile)
    )

    const isValid = missingFiles.length === 0

    return {
      isValid,
      missingFiles,
      rootFiles,
      rootFolders,
      message: isValid
        ? 'The ZIP file contains all required files'
        : `Missing files at ZIP root: ${missingFiles.join(', ')}`,
      skippedClientValidation: false,
    }
  } catch (error) {
    return {
      isValid: false,
      missingFiles: requiredFiles,
      rootFiles: [],
      rootFolders: [],
      message: 'Error reading ZIP file: ' + error.message,
      skippedClientValidation: false,
    }
  }
}

/**
 * Valide un fichier ZIP pour l'import de questions
 * Vérifie la présence de questions.xlsx à la racine
 * @param {File} zipFile - Le fichier ZIP à valider
 * @returns {Promise<{isValid: boolean, missingFiles: Array<string>, message: string}>}
 */
export async function validateQuestionsZip(zipFile) {
  return validateZipContents(zipFile, ['questions.xlsx'])
}

/**
 * Valide un fichier ZIP pour l'import de jeux
 * Vérifie la présence de games.txt à la racine
 * @param {File} zipFile - Le fichier ZIP à valider
 * @returns {Promise<{isValid: boolean, missingFiles: Array<string>, message: string}>}
 */
export async function validateGamesZip(zipFile) {
  return validateZipContents(zipFile, ['games.txt'])
}

/**
 * Valide un fichier ZIP pour l'import complet
 * Vérifie la présence de questions.xlsx ET games.txt à la racine
 * @param {File} zipFile - Le fichier ZIP à valider
 * @returns {Promise<{isValid: boolean, missingFiles: Array<string>, message: string}>}
 */
export async function validateCompleteZip(zipFile) {
  return validateZipContents(zipFile, ['questions.xlsx', 'games.txt'])
}
